/** Constituent countries of the United Kingdom — stored in profile `state` (same as US states). */
export const UK_NATIONS = ['England', 'Scotland', 'Wales', 'Northern Ireland'] as const;

export type UkNation = (typeof UK_NATIONS)[number];

/** Major cities by UK nation for the City dropdown when country is United Kingdom. */
export const UK_CITIES_BY_NATION: Record<UkNation, readonly string[]> = {
  England: [
    'Bath',
    'Birmingham',
    'Blackpool',
    'Bournemouth',
    'Bradford',
    'Brighton',
    'Bristol',
    'Cambridge',
    'Canterbury',
    'Carlisle',
    'Chelmsford',
    'Chester',
    'Coventry',
    'Derby',
    'Durham',
    'Exeter',
    'Gloucester',
    'Hull',
    'Ipswich',
    'Leeds',
    'Leicester',
    'Lincoln',
    'Liverpool',
    'London',
    'Manchester',
    'Milton Keynes',
    'Newcastle',
    'Northampton',
    'Norwich',
    'Nottingham',
    'Oxford',
    'Peterborough',
    'Plymouth',
    'Portsmouth',
    'Preston',
    'Reading',
    'Salford',
    'Sheffield',
    'Southampton',
    'Stoke-on-Trent',
    'Sunderland',
    'Truro',
    'Wolverhampton',
    'York',
  ],
  Scotland: [
    'Aberdeen',
    'Dundee',
    'Edinburgh',
    'Glasgow',
    'Inverness',
    'Paisley',
    'Perth',
    'Stirling',
  ],
  Wales: ['Bangor', 'Cardiff', 'Newport', 'St Davids', 'Swansea', 'Wrexham'],
  'Northern Ireland': ['Armagh', 'Bangor', 'Belfast', 'Derry', 'Lisburn', 'Newry'],
};

export function isUkNation(value: string): value is UkNation {
  return (UK_NATIONS as readonly string[]).includes(value);
}

export function citiesForUkNation(nation: string): readonly string[] {
  if (!isUkNation(nation)) return [];
  return UK_CITIES_BY_NATION[nation];
}

export function allUkCities(): string[] {
  return Array.from(new Set(UK_NATIONS.flatMap(nation => UK_CITIES_BY_NATION[nation]))).sort(
    (a, b) => a.localeCompare(b)
  );
}

/** Ceremonial counties / council areas — stored in profile `state` (same field as US states). */
export const UK_COUNTIES: readonly string[] = [
  ...UK_NATIONS,
  'Bedfordshire',
  'Berkshire',
  'Bristol',
  'Buckinghamshire',
  'Cambridgeshire',
  'Cheshire',
  'Cornwall',
  'County Antrim',
  'County Armagh',
  'County Down',
  'County Durham',
  'County Fermanagh',
  'County Londonderry',
  'County Tyrone',
  'Cumbria',
  'Derbyshire',
  'Devon',
  'Dorset',
  'East Riding of Yorkshire',
  'East Sussex',
  'Essex',
  'Gloucestershire',
  'Greater London',
  'Greater Manchester',
  'Hampshire',
  'Herefordshire',
  'Hertfordshire',
  'Isle of Wight',
  'Kent',
  'Lancashire',
  'Leicestershire',
  'Lincolnshire',
  'Merseyside',
  'Norfolk',
  'North Yorkshire',
  'Northamptonshire',
  'Northumberland',
  'Nottinghamshire',
  'Oxfordshire',
  'Rutland',
  'Shropshire',
  'Somerset',
  'South Yorkshire',
  'Staffordshire',
  'Suffolk',
  'Surrey',
  'Tyne and Wear',
  'Warwickshire',
  'West Midlands',
  'West Sussex',
  'West Yorkshire',
  'Wiltshire',
  'Worcestershire',
  'Aberdeen',
  'Aberdeenshire',
  'Angus',
  'Argyll and Bute',
  'Clackmannanshire',
  'Dumfries and Galloway',
  'Dundee',
  'East Ayrshire',
  'East Dunbartonshire',
  'East Lothian',
  'East Renfrewshire',
  'Edinburgh',
  'Falkirk',
  'Fife',
  'Glasgow',
  'Highland',
  'Inverclyde',
  'Midlothian',
  'Moray',
  'Na h-Eileanan Siar',
  'North Ayrshire',
  'North Lanarkshire',
  'Orkney',
  'Perth and Kinross',
  'Renfrewshire',
  'Scottish Borders',
  'Shetland',
  'South Ayrshire',
  'South Lanarkshire',
  'Stirling',
  'West Dunbartonshire',
  'West Lothian',
  'Blaenau Gwent',
  'Bridgend',
  'Caerphilly',
  'Cardiff',
  'Carmarthenshire',
  'Ceredigion',
  'Conwy',
  'Denbighshire',
  'Flintshire',
  'Gwynedd',
  'Isle of Anglesey',
  'Merthyr Tydfil',
  'Monmouthshire',
  'Neath Port Talbot',
  'Newport',
  'Pembrokeshire',
  'Powys',
  'Rhondda Cynon Taf',
  'Swansea',
  'Torfaen',
  'Vale of Glamorgan',
  'Wrexham',
];
